<template>
  <v-card flat>
    <v-card-title>
      <span class="title">Hourly Balances</span>
    </v-card-title>
    <v-card-text>
      <div class="mb-3 text-xs-right">
        <div class="d-inline-block" style="width: 210px">
          <v-text-field
            v-model="state.windowSize"
            suffix="%"
            type="number"
            :min="0"
            :max="100"
            label="Acceptable Diff:"
          ></v-text-field>
        </div>
      </div>
      <div v-if="loading" class="elevation-1 white text-xs-center pa-3" style="transform: translateY(0px);">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="!exchangesData.length" class="elevation-1 white text-xs-center pa-3" style="transform: translateY(0px);">
        <span>No data</span>
      </div>
      <v-expansion-panel v-else expand>
        <v-expansion-panel-content v-for="(exchange, i) in exchangesData" :key="i">
          <div slot="header">
            <div v-if="hourlyBalancesTableData[exchange['account_id']]">
              <v-icon
                v-html="'mdi-checkbox-blank-circle'"
                small
                :color="countNotInWindows(hourlyBalancesTableData[exchange['account_id']].timesData) > 0 ? 'error' : 'success'"
                class="mr-2"
              />
              <b> {{titleText(exchange['exchange'])}} </b> : {{titleText(exchange['account_id'])}}
              <span
                v-show="countNotInWindows(hourlyBalancesTableData[exchange['account_id']].timesData) > 0"
                class="caption error--text pl-1"
              >
                ({{ countNotInWindows(hourlyBalancesTableData[exchange['account_id']].timesData) }} unacceptable )
              </span>
            </div>
          </div>
          <v-card>
            <v-card-text>
              <div class="mb-3">
                <v-chip>
                  <b>Sell Amount:</b>
                  <span class="ml-1">{{ hourlyBalancesTableData[exchange['account_id']].amountToSell }}</span>
                </v-chip>
                <v-chip>
                  <b>Start Sell:</b>
                  <span class="ml-1">{{ hourlyBalancesTableData[exchange['account_id']].startTime }}</span>
                </v-chip>
                <v-chip>
                  <b>End Sell:</b>
                  <span class="ml-1">{{ hourlyBalancesTableData[exchange['account_id']].endTime }}</span>
                </v-chip>
                <v-chip>
                  <b>Daily To Exchange:</b>
                  <span class="ml-1">
                    <v-icon
                      v-html="'mdi-checkbox-blank-circle'"
                      small
                      :color="hourlyBalancesTableData[exchange['account_id']].dailyToExchange ? 'success' : 'error'"
                    />
                  </span>
                </v-chip>
              </div>
              <v-data-table
                :headers="hourlyBalancesTableHeaders"
                :items="hourlyBalancesTableData[exchange['account_id']].timesData"
                hide-actions
                class="elevation-1"
                disable-initial-sort

              >
                <template slot="items" slot-scope="props">
                  <td class="text-xs-right">{{ props.item.label }}</td>
                  <td class="text-xs-right">
                    <div v-if="props.item.min_balance !== undefined">{{ props.item.min_balance | dollar }}</div>
                    <div v-if="props.item['currency'].min_balance !== undefined">
                      <b style="display: inline-block">{{ props.item['currencyName'].toUpperCase() }}&nbsp;</b>
                      <span style="display: inline-block">{{ props.item['currency'].min_balance }}</span>
                    </div>
                  </td>
                  <td class="text-xs-right">
                    <div v-if="props.item.actual_balance_end !== undefined">{{ props.item.actual_balance_end | dollar }}</div>
                    <div v-if="props.item['currency'].actual_balance_end !== undefined">
                      <b style="display: inline-block">{{ props.item['currencyName'].toUpperCase() }}&nbsp;</b>
                      <span style="display: inline-block">{{ props.item['currency'].actual_balance_end }}</span>
                    </div>
                  </td>
                  <td class="text-xs-right">
                    <div v-if="props.item.min_balance !== undefined">{{ (isNaN(props.item.diff) ? '' : props.item.diff) | dollar }}</div>
                    <div v-if="props.item['currency'].min_balance !== undefined">
                      <b style="display: inline-block">{{ props.item['currencyName'].toUpperCase() }}&nbsp;</b>
                      <span style="display: inline-block">{{ isNaN(props.item['currency'].diff) ? '' : props.item['currency'].diff }}</span>
                    </div>
                  </td>
                  <td class="text-xs-right" :class="!isFinite(props.item.diff_percentage) ? 'gray--text' : (checkInWindows(props.item.diff_percentage) ? 'green--text' : 'red--text')">
                    <span v-if="!isFinite(props.item.diff_percentage)">-</span>
                    <span v-else>{{ props.item.diff_percentage > 0 ? `+${props.item.diff_percentage}` : props.item.diff_percentage }}</span>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-card-text>
  </v-card>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { titleText, padStart } from '../../libs/utils'
import MongoService from '../../services/mongo.service'
import Alert from '../../libs/alert'
export default {
  props: {
    data: { type: Object, required: true, default: () => ({}) },
    currency: { type: String, required: false, default: 'XRP' },
    date: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      state: {
        windowSize: 5
      },
      exchangesData: [],
      hourlyBalances: {}
    }
  },
  beforeMount () {
    const userState = this.$store.getters['user/balances']
    this.state.windowSize = userState['windowSize']
  },
  watch: {
    'state.windowSize' () {
      this.$store.dispatch('user/setBalances', { windowSize: +this.state.windowSize || 0 })
    },
    'data': {
      immediate: true,
      async handler (val) {
        this.loading = true
        this.$nextTick(async () => {
          const dailyToExchanges = _.get(val, 'daily_to_exchanges', []).filter(dailyExchange => dailyExchange['submitted_at'] !== undefined)
          this.exchangesData = await Promise.all(
            _.get(val, 'exchanges', [])
            .map(async exchange => {
              const accountId = _.get(exchange, 'account_id', '')
              const targetDailyExchange = dailyToExchanges.find(dailyExchange => dailyExchange['to_account_id'] === accountId)
              exchange['daily_to_exchange'] = targetDailyExchange
              const botConfigs = await MongoService.query.getBotConfigsByAccountId(accountId)
              exchange['bot-configs'] = botConfigs
              const hourlyBalances = await MongoService.balances.hourly(
                accountId,
                `bot-${botConfigs['type']}`,
                this.currency,
                new Date(`${this.date}T05:00:00.000Z`).toISOString()
              )
              exchange['hourly-balances'] = hourlyBalances
              return exchange
            })
          )
          this.loading = false
        })
      }
    }
  },
  computed: {
    windowSizeValue () {
      return +this.state.windowSize || 0
    },
    hourlyBalancesTableHeaders () {
      return [
        { text: 'Time', value: 'label', align: 'right', sortable: false },
        { text: 'Min Balance', value: 'min_balance', align: 'right', sortable: false },
        { text: 'Actual Balance', value: 'actual_balance_end', align: 'right', sortable: false },
        { text: 'Difference', value: 'diff', align: 'right', sortable: false },
        { text: '% Difference', value: 'diff_percentage', align: 'right', sortable: false }
      ]
    },
    hourlyBalancesTableData () {
      return this.exchangesData.reduce((c, exchange) => {
        try {
          const botConfigs = {
            startTime: _.get(exchange, 'bot-configs.configuration.selling_duration.start', 0),
            endTime: _.get(exchange, 'bot-configs.configuration.selling_duration.end', 0)
          }
          const startTime = moment.utc(`${this.date.split('T')[0]}T05:00:00.000Z`)
          const endTime = moment.utc(`${this.date.split('T')[0]}T05:00:00.000Z`).add(1, 'day')
          const numberOfHour = 24 + botConfigs.endTime - botConfigs.startTime
          // const numberOfHour = endTime.diff(startTime) / (3600 * 1000)
          const amountToSell = _.get(exchange, 'amount_to_sell', 0)
          const amountToSellPerHour = amountToSell / (numberOfHour || 1)
          const timesData = []
          let sTime = startTime.clone()
          const eTime = endTime.clone()
          for (let i = 0; sTime < eTime; sTime = sTime.add(1, 'hour'), i++) {
            const currentHour = +sTime.format('H')
            const hourlyBalances = _.get(exchange, 'hourly-balances', {})
            const hourlyBalance = _.get(hourlyBalances, ((currentHour + 24) % 24).toString(), {})
            const hourlyBalanceResultValue = {
              ...mapHourlyBalanceValue({ hourlyBalance, i, numberOfHour, sTime, amountToSell, amountToSellPerHour }),
              'currencyName': 'xrp',
              'currency': mapHourlyBalanceValue({ 'hourlyBalance': hourlyBalance['xrp'] || {}, i, numberOfHour, sTime, amountToSell, amountToSellPerHour })
            }
            timesData.push(hourlyBalanceResultValue)
          }
          c[exchange['account_id']] = {
            startTime: moment.utc(`${this.date.split('T')[0]}T${padStart(botConfigs.startTime, 2, '0')}:00:00.000Z`).format('YYYY-MM-DD, hA'),
            endTime: moment.utc(`${this.date.split('T')[0]}T${padStart(botConfigs.endTime, 2, '0')}:00:00.000Z`).add(1, 'day').format('YYYY-MM-DD, hA'),
            amountToSell,
            dailyToExchange: _.get(exchange, 'daily_to_exchange'),
            timesData
          }
        } catch (e) {
          Alert.error({ message: e.message || e.toString() })
        }
        return c
        // FUNCTIONS
        function mapHourlyBalanceValue ({ hourlyBalance, i, numberOfHour, sTime, amountToSell, amountToSellPerHour }) {
          const lastBalance = hourlyBalance['last_balance']
          const diffAmount = lastBalance - hourlyBalance['min']
          const diffPercentage = +(100 * diffAmount / (hourlyBalance['min'])).toFixed(2)
          let targetBalanceEnd = amountToSell - ((i + 1) * amountToSellPerHour)
          targetBalanceEnd = i >= numberOfHour ? 0 : targetBalanceEnd
          return {
            time: sTime.toDate(),
            label: sTime.format('YYYY-MM-DD, hA'),
            target_balance_start: amountToSell - (i * amountToSellPerHour),
            target_balance_end: targetBalanceEnd,
            actual_balance_end: lastBalance,
            min_balance: hourlyBalance['min'],
            diff: diffAmount,
            diff_percentage: diffPercentage,
            // diff_percentage_target: i >= numberOfHour ? 0 : (100 * (lastBalance - targetBalanceEnd) / (targetBalanceEnd || 1)).toFixed(2)
          }
        }
      }, {})
    }
  },
  methods: {
    titleText,
    checkInWindows (val) {
      if (isNaN(val)) return false
      if (!isFinite(val)) return true
      return Math.abs(val) <= this.windowSizeValue
    },
    countNotInWindows (arr) {
      return arr
        .filter(item => !isNaN(item.diff_percentage))
        .filter(item => !this.checkInWindows(item.diff_percentage))
        .length
    }
  }
}
</script>
